import React from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { MoreVertical } from "lucide-react";
import { Pagination } from "@/components/tables/Pagination";
import { Procedures } from "@/types/procedures";

interface ProceduresTableProps {
  procedures: Procedures[];
  paginatedProcedures: Procedures[];
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  setCurrentPage: (page: number) => void;
  canEdit: boolean;
  onEdit: (procedure: Procedures) => void;
  onRemove: (id: number) => void;
}

const ProceduresTable: React.FC<ProceduresTableProps> = ({
  procedures,
  paginatedProcedures,
  currentPage,
  totalPages,
  itemsPerPage,
  setCurrentPage,
  canEdit,
  onEdit,
  onRemove,
}) => {
  const paginationMeta = {
    total: procedures.length,
    per_page: itemsPerPage,
    last_page: 0,
    current_page: currentPage,
    links: [
      {
        url: currentPage > 1 ? String(currentPage - 1) : null,
        label: "‹",
        active: false,
      },
      ...Array.from({ length: totalPages }, (_, i) => ({
        url: String(i + 1),
        label: String(i + 1),
        active: currentPage === i + 1,
      })),
      {
        url: currentPage < totalPages ? String(currentPage + 1) : null,
        label: "›",
        active: false,
      },
    ],
  };

  return (
    <Box
      borderRadius="lg"
      border="1px"
      borderColor="gray.200"
      overflow="hidden"
    >
      <Box overflowX="auto">
        <Table variant="simple" size={{ base: "sm", md: "md" }}>
          <Thead bg="white">
            <Tr>
              <Th fontWeight="600" fontSize="14px" color="#0A1B39">
                Procedure name
              </Th>
              <Th fontWeight="600" fontSize="14px" color="#0A1B39">
                Description
              </Th>
              <Th fontWeight="600" fontSize="14px" color="#0A1B39" isNumeric>
                Price
              </Th>
              {canEdit && <Th w="50px"></Th>}
            </Tr>
          </Thead>

          <Tbody bg="white">
            {procedures.length === 0 ? (
              <Tr>
                <Td colSpan={4} textAlign="center" py={8}>
                  <Text color="gray.500" fontSize="sm">
                    No procedures added yet. Click "Add Procedure" to add one.
                  </Text>
                </Td>
              </Tr>
            ) : (
              paginatedProcedures.map((procedure) => (
                <Tr key={procedure.id}>
                  <Td fontSize={{ base: "xs", md: "sm" }}>{procedure.name}</Td>
                  <Td fontSize={{ base: "xs", md: "sm" }}>
                    {procedure.description}
                  </Td>
                  <Td fontSize={{ base: "xs", md: "sm" }} isNumeric>
                    ₱{procedure.price.toLocaleString()}
                  </Td>

                  {canEdit && (
                    <Td>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<MoreVertical size={16} />}
                          variant="ghost"
                          size="sm"
                          aria-label="Options"
                        />
                        <MenuList fontSize={{ base: "xs", md: "sm" }}>
                          <MenuItem onClick={() => onEdit(procedure)}>
                            Edit
                          </MenuItem>
                          <MenuItem
                            color="red.600"
                            onClick={() => onRemove(procedure.id || 0)}
                          >
                            Remove
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  )}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Pagination */}
      {procedures.length > 0 && (
        <Box borderTop="1px" borderColor="gray.200" p={3} bg="white">
          <Pagination
            metas={paginationMeta}
            onClick={(url) => {
              if (!url) return;
              setCurrentPage(Number(url));
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ProceduresTable;
